import { useEffect, useRef, useState } from 'react';
import { FaEnvelope, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/apiService';
import './ForceEmailGate.css';

const CODE_LENGTH = 6;

export default function ForceEmailGate({ children }) {
  const { user, loading, updateUser, logout } = useAuth();
  const [digits, setDigits] = useState(() => Array(CODE_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);
  const ensureTriedRef = useRef(false);

  const needsVerify = Boolean(user && !user.emailVerified && !user.mustChangePassword);

  useEffect(() => {
    if (!needsVerify || !user?.id || ensureTriedRef.current) return undefined;
    ensureTriedRef.current = true;
    let cancelled = false;
    const ensureCode = async () => {
      try {
        await authService.resendVerification();
        if (!cancelled) setCooldown(60);
      } catch {
        // Cooldown ou e-mail já enviado no cadastro
      }
    };
    ensureCode();
    return () => { cancelled = true; };
  }, [needsVerify, user?.id]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = window.setInterval(() => {
      setCooldown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  if (loading || !needsVerify) {
    return children;
  }

  const codeValue = digits.join('');

  const focusInput = (index) => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  };

  const handleDigitChange = (index, raw) => {
    const value = String(raw).replace(/\D/g, '');
    setError('');

    if (value.length > 1) {
      const chars = value.slice(0, CODE_LENGTH).split('');
      const next = [...digits];
      chars.forEach((char, offset) => {
        if (index + offset < CODE_LENGTH) next[index + offset] = char;
      });
      setDigits(next);
      focusInput(Math.min(index + chars.length, CODE_LENGTH - 1));
      return;
    }

    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    if (value && index < CODE_LENGTH - 1) focusInput(index + 1);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');

    if (!/^\d{6}$/.test(codeValue)) {
      setError('Digite o código de 6 dígitos enviado ao seu e-mail.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authService.verifyEmailCode(codeValue);
      updateUser(response?.data?.user || null);
    } catch (err) {
      setError(err.message || 'Código inválido.');
      setDigits(Array(CODE_LENGTH).fill(''));
      focusInput(0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setError('');
    setInfo('');
    setResending(true);
    try {
      const result = await authService.resendVerification();
      setInfo(result?.message || 'Novo código enviado.');
      setCooldown(60);
      setDigits(Array(CODE_LENGTH).fill(''));
      focusInput(0);
    } catch (err) {
      setError(err.message || 'Não foi possível reenviar o código.');
      const match = String(err.message || '').match(/(\d+)s/);
      if (match) setCooldown(Number(match[1]));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="force-email-gate">
      <div className="force-email-card">
        <div className="force-email-icon" aria-hidden="true">
          <FaShieldAlt />
        </div>
        <p className="force-email-eyebrow">Verificação de e-mail</p>
        <h1>Confirme sua conta</h1>
        <p className="force-email-lead">
          Enviamos um código de 6 dígitos para <strong>{user.email}</strong>.
          Digite-o abaixo para liberar o acesso.
        </p>

        <form className="force-email-form" onSubmit={handleSubmit}>
          <div className="force-email-inputs" role="group" aria-label="Código de verificação">
            {digits.map((digit, index) => (
              <input
                key={`otp-${index}`}
                ref={(el) => { inputsRef.current[index] = el; }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={index === 0 ? CODE_LENGTH : 1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                aria-label={`Dígito ${index + 1}`}
                disabled={submitting}
              />
            ))}
          </div>

          {error && <p className="force-email-error">{error}</p>}
          {info && !error && <p className="force-email-info">{info}</p>}

          <button type="submit" className="force-email-submit" disabled={submitting || codeValue.length < CODE_LENGTH}>
            {submitting ? <FaSpinner className="force-email-spin" /> : <FaEnvelope />}
            {submitting ? 'Verificando...' : 'Confirmar e-mail'}
          </button>
        </form>

        <button
          type="button"
          className="force-email-resend"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
        >
          {resending
            ? 'Reenviando...'
            : cooldown > 0
              ? `Reenviar em ${cooldown}s`
              : 'Reenviar código'}
        </button>

        <button type="button" className="force-email-logout" onClick={() => logout()}>
          Sair da conta
        </button>
      </div>
    </div>
  );
}
